/**
 * Pulls: the route out of the collection.
 *
 * Cards are filed wherever the open bin was, so they are *not* findable by
 * searching a shelf. The only thing that makes them cheap to retrieve is walking
 * to fewer bins, and the API's `min_bins` strategy is what computes that route -
 * one container at a time, bins inside it, slots in order. This screen renders the
 * plan it returns and lets the user tick positions off as they pull them.
 *
 * The addresses on this screen come from the picklist items, which the API built
 * from the copies' own location records: the same address the scan reported when
 * the card was filed. Nothing here re-derives a location from a bin label, because
 * a plausible-looking address that is not the card's is worse than none.
 */

import { useMemo, useState } from 'react';
import { ApiError, type Api } from '../api/http.js';
import type { Picklist, PicklistItem, Shortfall } from '../api/types.js';
import { Banner } from '../components/Banner.js';
import { PrintingPicker } from '../components/PrintingPicker.js';
import { printingLabel, type ChosenPrinting } from '../scan/queue.js';

export interface PicklistsScreenProps {
  api: Api;
  /** What this session filed, so pulling it back out needs no retyping. */
  filed: ChosenPrinting[];
  onPulled: () => void;
}

interface Wanted {
  printingId: number;
  label: string;
  quantity: number;
}

export function PicklistsScreen({ api, filed, onPulled }: PicklistsScreenProps) {
  const [wanted, setWanted] = useState<Wanted[]>([]);
  const [label, setLabel] = useState('');
  const [open, setOpen] = useState<{ picklist: Picklist; items: PicklistItem[] } | null>(null);
  const [shortfalls, setShortfalls] = useState<Shortfall[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  /** What this session filed, one row per printing with how many copies of it. */
  const recent = useMemo(() => {
    const grouped = new Map<number, { printing: ChosenPrinting; copies: number }>();
    for (const printing of filed) {
      const existing = grouped.get(printing.printingId);
      if (existing) existing.copies += 1;
      else grouped.set(printing.printingId, { printing, copies: 1 });
    }
    return [...grouped.values()];
  }, [filed]);

  const add = (printing: ChosenPrinting, copies = 1): void => {
    setWanted((current) => {
      const existing = current.find((entry) => entry.printingId === printing.printingId);
      if (existing) {
        return current.map((entry) =>
          entry.printingId === printing.printingId ? { ...entry, quantity: entry.quantity + copies } : entry,
        );
      }
      return [...current, { printingId: printing.printingId, label: printingLabel(printing), quantity: copies }];
    });
  };

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    if (wanted.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.createPicklist({
        label: label === '' ? `Pull ${wanted.length} card${wanted.length === 1 ? '' : 's'}` : label,
        strategy: 'min_bins',
        requests: wanted.map((entry) => ({ printingId: entry.printingId, quantity: entry.quantity })),
      });
      setOpen({ picklist: created.picklist, items: created.items });
      setShortfalls(created.shortfalls);
      setWanted([]);
      setLabel('');
      onPulled();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'the picklist could not be built');
    } finally {
      setBusy(false);
    }
  };

  const pick = async (position: number) => {
    if (!open) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.markPicked(open.picklist.publicId, position);
      setOpen(updated);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'that position could not be ticked off');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <h2>Pulls</h2>
      <p className="hint">
        Name the cards you want and the app works out the shortest walk: one container at a time, bins inside it, slots
        in order. Tick each card off as you take it out.
      </p>

      {error && <Banner kind="error">{error}</Banner>}
      {shortfalls.length > 0 && (
        <Banner kind="warning">
          {shortfalls
            .map((shortfall) => `${shortfall.requested} wanted, ${shortfall.allocated} on a shelf (printing ${shortfall.printingId})`)
            .join('; ')}
        </Banner>
      )}

      <form className="card" onSubmit={(event) => void create(event)}>
        <h3>Build a pull list</h3>

        {wanted.length === 0 ? (
          <p className="hint">Nothing on the list yet.</p>
        ) : (
          <ul className="items">
            {wanted.map((entry) => (
              <li key={entry.printingId}>
                <span className="bin-name">{entry.label}</span>
                <span className="stepper">
                  <button
                    type="button"
                    className="ghost"
                    aria-label={`One fewer ${entry.label}`}
                    onClick={() => setWanted((current) => current.map((item) => (item.printingId === entry.printingId ? { ...item, quantity: Math.max(1, item.quantity - 1) } : item)))}
                  >
                    −
                  </button>
                  <strong aria-live="polite">{entry.quantity}</strong>
                  <button
                    type="button"
                    className="ghost"
                    aria-label={`One more ${entry.label}`}
                    onClick={() => setWanted((current) => current.map((item) => (item.printingId === entry.printingId ? { ...item, quantity: item.quantity + 1 } : item)))}
                  >
                    +
                  </button>
                  <button
                    type="button"
                    className="ghost"
                    aria-label={`Remove ${entry.label} from the list`}
                    onClick={() => setWanted((current) => current.filter((item) => item.printingId !== entry.printingId))}
                  >
                    Remove
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}

        <label>
          Pull list label
          <input
            name="label"
            autoComplete="off"
            spellCheck={false}
            placeholder="Game night deck…"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            maxLength={160}
          />
        </label>

        <button type="submit" className="primary" disabled={busy || wanted.length === 0}>
          {busy ? 'Planning…' : 'Build the list'}
        </button>
      </form>

      <div className="card">
        <h3>Add a card</h3>
        {adding ? (
          <>
            <PrintingPicker api={api} onChoose={add} prompt="The card says" />
            <button type="button" className="ghost" onClick={() => setAdding(false)}>
              Done adding
            </button>
          </>
        ) : (
          <>
            {recent.length > 0 && (
              <>
                <p className="hint">Filed in this session - pull one back out:</p>
                <ul className="candidates">
                  {recent.slice(0, 8).map((entry) => (
                    <li key={entry.printing.printingId}>
                      <button type="button" onClick={() => add(entry.printing, entry.copies)}>
                        {printingLabel(entry.printing)}
                        {entry.copies > 1 && <span className="hint"> · {entry.copies} copies filed</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <button type="button" className="ghost" onClick={() => setAdding(true)}>
              Add by set code and number
            </button>
          </>
        )}
      </div>

      {open && (
        <div className="card walk">
          <header className="row">
            <h3>{open.picklist.label}</h3>
            <span className="hint tnum">
              {open.picklist.binsTouched} bin{open.picklist.binsTouched === 1 ? '' : 's'} · {open.picklist.picked}/
              {open.picklist.picked + open.picklist.remaining} pulled · {open.picklist.status}
            </span>
          </header>
          <ol className="items">
            {open.items.map((item) => (
              <li key={item.position} className={item.pickedAt === null ? '' : 'done'}>
                <span className="position">{item.position}</span>
                <span className="grow walk-address">{item.address ?? 'no longer on a shelf'}</span>
                {item.pickedAt === null ? (
                  <button type="button" className="primary" disabled={busy} onClick={() => void pick(item.position)}>
                    Pulled
                  </button>
                ) : (
                  <span className="hint">pulled</span>
                )}
              </li>
            ))}
          </ol>
          <p className="hint">
            Position 1 is the first card the walk reaches. Each address is the copy's own record, the same one the scan
            reported when it was filed.
          </p>
        </div>
      )}
    </div>
  );
}
